import { useCallback, useContext, useMemo, useState } from "react";

import CurrencyContext from "../contexts/CurrencyContext";
import ProductsContext from "../contexts/ProductsContext";

import Card, { useThreshold } from "./Card";
import { ProductCardInner, productLink } from "./ProductCard";
import handleConfirmation from "../utils/adaHandler";
import { HTMLSectionWithLink } from "./elements/Link";
import {
  checkForAllSoldoutVariants,
  getAddedOffers,
  getProductOriginalPrice,
  updateOffer,
} from "../core/products";
import Img from "./elements/Img";

import withFallback from "../common/image-with-fallback";
import Prices from "./elements/Prices";
import { Offer, Product } from "../domain";
import { getProductImageAspectRatio } from "./utils";
import classNames from "classnames";
import firstAvailableVariantInOffer from "../utils/firstAvailableVariant";
import getSelectedProductVariantId from "../utils/getSelectedVariant";
import AnimationContext from "../contexts/AnimationContext";
import { getAvailableOfferTargetVariantsSet } from "../core/products/variantUtils";

const ProductCardSection = ({
  passed,
  offer,
  offerProduct,
  onClick,
  display_product_names_as_links,
}) => {
  const currencyCtx = useContext(CurrencyContext);

  const productCtx = useContext(ProductsContext);

  const product = productCtx.getProduct(offerProduct.shopify_product_handle);
  const targetProduct = offer?.target_shopify_product_handle
    ? productCtx.getTargetProduct(offer.target_shopify_product_handle)
    : undefined;

  const areAllOfferVariantsUnavailable = checkForAllSoldoutVariants(
    product,
    offer
  );

  if (
    !product ||
    !product.loaded ||
    !product.available ||
    areAllOfferVariantsUnavailable ||
    (offer?.target_shopify_product_handle &&
      getAvailableOfferTargetVariantsSet(offer, targetProduct).size === 0)
  ) {
    return null;
  }

  const variant = firstAvailableVariantInOffer(product, offer);
  const imageAspectRatio = useMemo(
    () => getProductImageAspectRatio(variant, product),
    [variant, product]
  );

  const animationContext = useContext(AnimationContext);

  return (
    <div
      className={`pt-4 first:pt-0 items-center flex gap-4 Giftbox-ProductCardSection Product-Id-${product.id}`}
      data-cy="multiple-offer-popup"
    >
      <Img
        className={classNames(`[&>img]:aspect-${imageAspectRatio}`, {
          "bg-transparent": imageAspectRatio !== "1",
          "hover:cursor-pointer": true,
        })}
        onClick={() => {
          if (!product?.url) return;
          window.open(product.url, "_parent");
        }}
      >
        <img
          src={withFallback(
            (variant && variant.featured_image && variant.featured_image.src) ||
              product.featured_image
          )}
          alt={product?.title || "Product"}
        />
      </Img>
      <div className="GiftBox-Offer-Card-Line flex-1 flex flex-col justify-center Giftbox-CardProductNameWidth">
        {productLink(
          product,
          product.title,
          display_product_names_as_links,
          offer.gift_quantity
        )}
        <Prices
          originalPrice={variant ? getProductOriginalPrice(variant) : NaN}
          currency={currencyCtx}
        />
      </div>
      {passed && (
        <button
          className="GiftBox-Select-Gift-Button p-2.5 rounded-[0.6rem] self-center bg-primary-600 hover:bg-primary-700 active:bg-primary-800 disabled:bg-gray-200"
          onClick={() => {
            animationContext.triggerWithAnimation?.({
              name: "product_choice_added",
              delayMsBefore: 150,
              delayMsAfter: 120,
              callback: () => onClick(product),
            });
          }}
          tabIndex={0}
          onKeyPress={() => handleConfirmation(onClick)}
          data-cy="select-gift-button"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10 2C9.44772 2 9 2.44772 9 3V9H3C2.44772 9 2 9.44772 2 10C2 10.5523 2.44772 11 3 11H9V17C9 17.5523 9.44772 18 10 18C10.5523 18 11 17.5523 11 17V11H17C17.5523 11 18 10.5523 18 10C18 9.44772 17.5523 9 17 9H11V3C11 2.44772 10.5523 2 10 2Z"
              fill="white"
            />
          </svg>
        </button>
      )}
    </div>
  );
};

type Props = {
  offer: Offer;
};

function createSubtitle(offer: Offer, passed: boolean | undefined) {
  const subtitle = passed
    ? offer.subtitle_condition_met_product_selection || `Don't miss your reward`
    : offer.subtitle_condition_not_met || `and select one of the free gifts`;

  return (
    <HTMLSectionWithLink>
      {subtitle.replace("PRODUCTLINK", offer.target_shopify_product_name || "")}
    </HTMLSectionWithLink>
  );
}

const MultipleProductCard = ({ offer }: Props) => {
  const [selectedProductVariantId, setSelectedProductVariantId] = useState<
    number | undefined
  >(() => getSelectedProductVariantId(offer, getAddedOffers()));

  async function selectProduct(product: Product) {
    const product_item = offer.products?.find(
      (p) => p.shopify_product_handle === product.handle
    );
    offer.shopify_product_variants = product_item
      ? product_item.shopify_product_variants
      : [];

    // preselect first selectable
    offer.shopify_product_id = product_item
      ? product_item.shopify_product_id
      : null;
    const variant = firstAvailableVariantInOffer(product, offer);
    offer.shopify_product_variant_id = variant?.id;
    offer.shopify_product_handle = product.handle;
    // product_item is MultipleProductOfferProduct
    await updateOffer(offer, variant, product.handle);
    setSelectedProductVariantId(variant?.id);
  }

  const clearSelection = useCallback(async () => {
    offer.shopify_product_variant_id = undefined;
    offer.shopify_product_handle = undefined;
    offer.shopify_product_id = null;
    offer.shopify_product_variants = [];
    setSelectedProductVariantId(undefined);
    await updateOffer(offer, undefined);
  }, [offer]);

  const { passed } = useThreshold(offer);

  if (selectedProductVariantId) {
    return <ProductCardInner offer={offer} onEdit={clearSelection} />;
  }

  return (
    <Card
      offer={offer}
      isMultipleProduct
      subtitle={createSubtitle(offer, passed)}
    >
      <div className="grid grid-cols-1 gap-y-4 divide-y divide-gray-200">
        {
          // offer.products should exist, but we will handle this defensively for extra safety
          offer.products?.map((product) => (
            <ProductCardSection
              offer={offer}
              key={product.shopify_product_id}
              offerProduct={product}
              passed={passed}
              display_product_names_as_links={
                offer.display_product_names_as_links
              }
              onClick={selectProduct}
            />
          ))
        }
      </div>
    </Card>
  );
};

export default MultipleProductCard;
